/**
 * Appearance: piece sets, board styles and site themes.
 *
 * The first entry of every list is the default and matches the site exactly
 * as it shipped before the appearance chooser existed. Choices are stored on
 * this device only and applied as data attributes on <html>, which the
 * stylesheets key on. Function never changes with appearance; only looks do.
 */

export type PieceSetId =
  "staunty" | "standard" | "monolith" | "letterpress" | "deco" | "ink" | "pixel";
export type BoardStyleId =
  "classic" | "walnut" | "slate" | "marble" | "tournament" | "porcelain" | "obsidian";
export type SiteThemeId = "editorial" | "midnight" | "gallery" | "manuscript" | "glacier";

export type PieceSet = {
  id: PieceSetId;
  name: string;
  blurb: string;
  /** 1–5. How instantly each piece is identified, even at small sizes. */
  readability: number;
  /** 1–5. How much the set rewards looking at it. */
  beauty: number;
  /** Sprite URL for cm-chessboard (absolute, served from public/ or vendor/). */
  file: string;
  preview: string;
  credit: string;
};

export type BoardStyle = {
  id: BoardStyleId;
  name: string;
  blurb: string;
  light: string;
  dark: string;
};

export type SiteTheme = {
  id: SiteThemeId;
  name: string;
  blurb: string;
  swatches: string[];
  /** Meta theme-color for the browser chrome. */
  chrome: string;
};

export const PIECE_SETS: readonly PieceSet[] = [
  {
    id: "staunty",
    name: "Staunty",
    blurb: "The default. Soft, rounded, instantly readable at any size.",
    readability: 5,
    beauty: 3,
    file: "/vendor/cm-chessboard/assets/pieces/staunty.svg",
    preview: "/pieces/staunty-preview.svg",
    credit: "lichess Staunty, CC BY-NC-SA 4.0",
  },
  {
    id: "standard",
    name: "Classic",
    blurb: "The tournament-book drawing most players grew up with.",
    readability: 5,
    beauty: 3,
    file: "/vendor/cm-chessboard/assets/pieces/standard.svg",
    preview: "/pieces/standard-preview.svg",
    credit: "Cburnett, CC BY-SA 3.0",
  },
  {
    id: "monolith",
    name: "Monolith",
    blurb: "Flat, geometric silhouettes with a thin contrasting edge. Modern and calm.",
    readability: 4,
    beauty: 4,
    file: "/pieces/monolith.svg",
    preview: "/pieces/monolith-preview.svg",
    credit: "First 15 Moves original",
  },
  {
    id: "letterpress",
    name: "Letterpress",
    blurb: "Stencil initials on roundels. Nothing is faster to read; the charm is typographic.",
    readability: 5,
    beauty: 2,
    file: "/pieces/letterpress.svg",
    preview: "/pieces/letterpress-preview.svg",
    credit: "First 15 Moves original",
  },
  {
    id: "deco",
    name: "Deco",
    blurb:
      "Art Deco: stepped bases, sunburst crowns and gold inlay. Ornate, a touch slower to read.",
    readability: 3,
    beauty: 5,
    file: "/pieces/deco.svg",
    preview: "/pieces/deco-preview.svg",
    credit: "First 15 Moves original",
  },
  {
    id: "ink",
    name: "Ink",
    blurb:
      "Brush calligraphy. Loose, organic forms with a wet edge; beautiful, and the least literal.",
    readability: 2,
    beauty: 5,
    file: "/pieces/ink.svg",
    preview: "/pieces/ink-preview.svg",
    credit: "First 15 Moves original",
  },
  {
    id: "pixel",
    name: "Pixel",
    blurb: "Eight-bit pieces on a sixteen-square grid. Playful, crisp, and surprisingly readable.",
    readability: 4,
    beauty: 3,
    file: "/pieces/pixel.svg",
    preview: "/pieces/pixel-preview.svg",
    credit: "First 15 Moves original",
  },
];

export const BOARD_STYLES: readonly BoardStyle[] = [
  {
    id: "classic",
    name: "Classic",
    blurb: "The default. Warm tan and cream, built for legibility.",
    light: "#ecdab9",
    dark: "#c5a076",
  },
  {
    id: "walnut",
    name: "Walnut",
    blurb: "Oiled wood with a faint grain. Flatters the Staunty, Classic and Deco sets.",
    light: "#e6cfa6",
    dark: "#8f6039",
  },
  {
    id: "slate",
    name: "Slate",
    blurb: "Cool graphite and fog. A quiet stage for Monolith and Letterpress.",
    light: "#cfd6db",
    dark: "#6f7f8d",
  },
  {
    id: "marble",
    name: "Marble",
    blurb: "Ivory and sea-grey stone with soft veining. Gentle enough for the Ink set.",
    light: "#f3efe6",
    dark: "#aab6ae",
  },
  {
    id: "tournament",
    name: "Tournament",
    blurb: "The green-and-buff vinyl of club halls. Pixel and Classic feel at home here.",
    light: "#eeeed2",
    dark: "#769656",
  },
  {
    id: "porcelain",
    name: "Porcelain",
    blurb: "Blue-and-white glaze. Crisp, cool, and kind to dark pieces.",
    light: "#e4ecf3",
    dark: "#7f9db9",
  },
  {
    id: "obsidian",
    name: "Obsidian",
    blurb:
      "Deep graphite for the Midnight theme. Light squares stay bright enough to read black pieces.",
    light: "#8b96a1",
    dark: "#4a5560",
  },
];

export const SITE_THEMES: readonly SiteTheme[] = [
  {
    id: "editorial",
    name: "Editorial",
    blurb: "The default. Cream paper, deep sage, a serif headline.",
    swatches: ["#fbf7ef", "#2f6b5a", "#2a2623", "#c79a3c"],
    chrome: "#f5efe4",
  },
  {
    id: "midnight",
    name: "Midnight",
    blurb: "A dark study: ink-green surfaces, brass accents, cream type.",
    swatches: ["#121816", "#c3a661", "#efe9dc", "#8fb3a0"],
    chrome: "#121816",
  },
  {
    id: "gallery",
    name: "Gallery",
    blurb: "Swiss modern: white, black, hairlines, one vermilion accent, no curves.",
    swatches: ["#ffffff", "#111111", "#c5301f", "#f4f4f2"],
    chrome: "#ffffff",
  },
  {
    id: "manuscript",
    name: "Manuscript",
    blurb: "Parchment, oxblood and gold leaf, with double rules and an italic voice.",
    swatches: ["#f1e6cf", "#7a2323", "#2b1d12", "#b8862b"],
    chrome: "#f1e6cf",
  },
  {
    id: "glacier",
    name: "Glacier",
    blurb: "Pale blue light, frosted glass surfaces, pill buttons, airy rounded corners.",
    swatches: ["#eef3f7", "#235f88", "#16232e", "#e39a3b"],
    chrome: "#eef3f7",
  },
];

export type Appearance = {
  pieces: PieceSetId;
  board: BoardStyleId;
  theme: SiteThemeId;
};

export const DEFAULT_APPEARANCE: Appearance = {
  pieces: PIECE_SETS[0].id,
  board: BOARD_STYLES[0].id,
  theme: SITE_THEMES[0].id,
};

const STORAGE_KEY = "f15m_appearance";

type StorageLike = Pick<Storage, "getItem" | "setItem">;

function storage(): StorageLike | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function pieceSetById(id: string | null | undefined): PieceSet {
  return PIECE_SETS.find((set) => set.id === id) || PIECE_SETS[0];
}

export function boardStyleById(id: string | null | undefined): BoardStyle {
  return BOARD_STYLES.find((board) => board.id === id) || BOARD_STYLES[0];
}

export function siteThemeById(id: string | null | undefined): SiteTheme {
  return SITE_THEMES.find((theme) => theme.id === id) || SITE_THEMES[0];
}

/** Anything unknown falls back to the default, so a stale value never breaks a page. */
export function normalizeAppearance(raw: unknown): Appearance {
  const source = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return {
    pieces: pieceSetById(typeof source.pieces === "string" ? source.pieces : null).id,
    board: boardStyleById(typeof source.board === "string" ? source.board : null).id,
    theme: siteThemeById(typeof source.theme === "string" ? source.theme : null).id,
  };
}

export function loadAppearance(source: StorageLike | null = storage()): Appearance {
  if (!source) return { ...DEFAULT_APPEARANCE };
  try {
    const raw = source.getItem(STORAGE_KEY);
    return normalizeAppearance(raw ? JSON.parse(raw) : null);
  } catch {
    return { ...DEFAULT_APPEARANCE };
  }
}

export function saveAppearance(value: Appearance, target: StorageLike | null = storage()): void {
  if (!target) return;
  try {
    target.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* Storage can be unavailable in private browsing; the choice still applies. */
  }
}

type DocumentLike = {
  documentElement: { setAttribute(name: string, value: string): void };
  querySelector(selector: string): { setAttribute(name: string, value: string): void } | null;
};

/** Apply the choice to the page. Stylesheets key on these attributes. */
export function applyAppearance(
  value: Appearance,
  doc: DocumentLike | null = typeof document === "undefined" ? null : document
): void {
  if (!doc) return;
  doc.documentElement.setAttribute("data-theme", value.theme);
  doc.documentElement.setAttribute("data-board", value.board);
  doc.documentElement.setAttribute("data-piece-set", value.pieces);
  doc
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", siteThemeById(value.theme).chrome);
}

// ---- A tiny external store so React and the board share one source of truth. ----

let current: Appearance = loadAppearance();
const listeners = new Set<() => void>();

export function getAppearance(): Appearance {
  return current;
}

export function setAppearance(patch: Partial<Appearance>): Appearance {
  const next = normalizeAppearance({ ...current, ...patch });
  if (
    next.pieces === current.pieces &&
    next.board === current.board &&
    next.theme === current.theme
  ) {
    return current;
  }
  current = next;
  saveAppearance(current);
  applyAppearance(current);
  listeners.forEach((listener) => listener());
  return current;
}

export function resetAppearance(): Appearance {
  return setAppearance({ ...DEFAULT_APPEARANCE });
}

export function subscribeAppearance(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Used by tests to start from a clean slate. */
export function __resetAppearanceStoreForTests(
  value: Appearance = { ...DEFAULT_APPEARANCE }
): void {
  current = normalizeAppearance(value);
}
